import React, { createContext, useContext, useState } from 'react';
import { cn } from '../../lib/utils';
import { ChevronDown } from 'lucide-react';

const AccordionContext = createContext({ value: null, onValueChange: () => {} });

export function Accordion({ type = 'single', collapsible = false, value, defaultValue, onValueChange, className, children, ...props }) {
  const [internal, setInternal] = useState(defaultValue || null);
  const controlled = value !== undefined;
  const active = controlled ? value : internal;

  const handleChange = (v) => {
    let next;
    if (type === 'single') {
      next = active === v && collapsible ? null : v;
    } else {
      // multiple: active is an array
      const arr = Array.isArray(active) ? active : [];
      next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
    }
    if (!controlled) setInternal(next);
    onValueChange && onValueChange(next);
  };

  const isOpen = (v) => {
    if (type === 'multiple') return Array.isArray(active) && active.includes(v);
    return active === v;
  };

  return (
    <AccordionContext.Provider value={{ isOpen, onValueChange: handleChange }}>
      <div className={cn("w-full", className)} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

export function AccordionItem({ value, className, children, ...props }) {
  return (
    <div
      data-value={value}
      className={cn("border-b border-border", className)}
      {...props}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child, { _accordionValue: value }) : child
      )}
    </div>
  );
}

export function AccordionTrigger({ className, children, _accordionValue, ...props }) {
  const { isOpen, onValueChange } = useContext(AccordionContext);
  const open = isOpen(_accordionValue);

  return (
    <button
      type="button"
      onClick={() => onValueChange(_accordionValue)}
      className={cn(
        "flex w-full items-center justify-between py-4 text-sm font-medium text-foreground hover:text-primary transition",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown
        className={cn("h-4 w-4 text-muted-foreground transition-transform duration-200", open && "rotate-180")}
      />
    </button>
  );
}

export function AccordionContent({ className, children, _accordionValue, ...props }) {
  const { isOpen } = useContext(AccordionContext);
  const open = isOpen(_accordionValue);

  return (
    <div
      className={cn(
        "overflow-hidden text-sm transition-all",
        open ? "max-h-[1000px] pb-4" : "max-h-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}
